import type { RouteLocationRaw } from 'vue-router'
import type { Notification } from '@/types/notifications'

/**
 * Given a notification, returns the route to navigate to when clicked.
 * Returns null if no navigation target can be determined.
 */
export function notificationTarget(
  n: Pick<Notification, 'notification_type' | 'contract_id' | 'action_plan_id'>,
): RouteLocationRaw | null {
  // reajuste types -> contract detail with reajustes tab
  if (n.notification_type === 'reajuste_pendiente' || n.notification_type === 'reajuste_aplicado') {
    if (n.contract_id) {
      return { name: 'contract-detail', params: { id: n.contract_id }, query: { tab: 'reajustes' } }
    }
    return null
  }

  // action plan types -> action plan detail
  if (n.action_plan_id) {
    return { name: 'action-plan-detail', params: { id: n.action_plan_id } }
  }

  // contract types -> contract detail
  if (n.contract_id) {
    return { name: 'contract-detail', params: { id: n.contract_id } }
  }

  return null
}

/**
 * Portal-side counterpart of `notificationTarget`.
 *
 * The portal serializer exposes `contract_id` and `meeting_minute_id` (no
 * `action_plan_id`), and the supplier portal has no action plan detail view, so a
 * navigable notification resolves to `portal-meeting-minute-detail` or
 * `portal-contract-detail`.
 */
export function portalNotificationTarget(
  n: Pick<Notification, 'notification_type' | 'contract_id' | 'meeting_minute_id'>,
): RouteLocationRaw | null {
  // meeting minute types -> portal meeting minute detail
  if (n.meeting_minute_id) {
    return { name: 'portal-meeting-minute-detail', params: { id: n.meeting_minute_id } }
  }

  if (!n.contract_id) return null

  // reajuste types -> portal contract detail with reajustes tab
  if (n.notification_type === 'reajuste_pendiente' || n.notification_type === 'reajuste_aplicado') {
    return {
      name: 'portal-contract-detail',
      params: { id: n.contract_id },
      query: { tab: 'reajustes' },
    }
  }

  return { name: 'portal-contract-detail', params: { id: n.contract_id } }
}

/**
 * Returns the Material icon name for a notification type.
 */
export function notificationIcon(type: string): string {
  switch (type) {
    case 'score_red': return 'warning'
    case 'score_yellow': return 'info'
    case 'measurement_missing': return 'schedule'
    case 'action_plan_suggested': return 'assignment_add'
    case 'action_plan_item_assigned': return 'person_add'
    case 'action_plan_assigned': return 'person_add'
    case 'action_plan_due_soon': return 'schedule'
    case 'action_plan_overdue': return 'warning'
    case 'action_plan_closed': return 'check_circle'
    case 'reajuste_pendiente': return 'currency_exchange'
    case 'reajuste_aplicado': return 'price_check'
    default: return 'notifications'
  }
}
